import { MouseEvent, useRef, useState, useMemo, ChangeEventHandler, useContext } from 'react';
import styled from '@emotion/styled';
import {
  alpha,
  Box,
  Checkbox,
  Collapse,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';

import { ConfigContext } from '@/contextAPI/configContext';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { CustomerProfile } from '@/redux/slices/slice_types';
import { applyFilter } from '@/redux/slices/dataTableSlice';
import { getDeletedProfiles, setShowDeletedProfilesInJobsFilter } from '@/redux/slices/profileSlice';
import { selectDataTableFilterOptions, selectDataTableFilters } from '@/redux/selectors/dataTableSelectors';
import { selectAllProfilesByCustomer, selectDeletedProfiles, selectShowDeletedProfilesInJobsFilter } from '@/redux/selectors/profileSelectors';
import { selectRawCustomerList } from '@/redux/selectors/constantsSelectors';
import { truncateText } from '@/_utils/utils';
import PolarisIcon from '@/components/_common/PolarisIcon';
import FilterAccordion from '@/components/_common/Filters/FilterAccordion';
import {
  ClearSearchButton,
  CollapseButton,
  CollapseWrapper,
  ExpandButton,
  SearchField,
  SelectedFilterChip,
  SelectedFiltersContainer,
  SelectedOptionsCount,
  StyledBox
} from '@/components/_common/Filters/FilterMultiSelect';

interface FilterProfilesProps {
  isQuickFilters?: boolean;
  expandedFilters?: string[];
  onExpand?: (filter: string) => void;
  isAdvanceFilter?: boolean;
  advanceFilters?: Record<string, any>;
  onAdvanceFiltersChange?: (newAdvanceFilters: { type: string; selections: any }) => void;
}

const FilterProfiles = ({
  isQuickFilters = false,
  expandedFilters = [],
  onExpand,
  isAdvanceFilter = false,
  advanceFilters = {},
  onAdvanceFiltersChange
}: FilterProfilesProps) => {
  const isFiltersV2 = useContext(ConfigContext)?.filtersVersion === 2;
  const dispatch = useAppDispatch();
  const filters = useAppSelector(selectDataTableFilters);
  const customerList = useAppSelector(selectRawCustomerList);
  const allProfiles = useAppSelector(selectAllProfilesByCustomer);
  const deletedProfiles = useAppSelector(selectDeletedProfiles);
  const showDeletedProfiles = useAppSelector(selectShowDeletedProfilesInJobsFilter);
  const filterOptions = useAppSelector(selectDataTableFilterOptions('profile'));
  const [searchTerm, setSearchTerm] = useState('');
  const [optionsExpanded, setOptionsExpanded] = useState(false);
  const [expandedStates, setExpandedStates] = useState<Record<string, boolean>>({});
  const [selectedFiltersExpanded, setSelectedFiltersExpanded] = useState(false);
  const searchBoxRef = useRef<HTMLInputElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const filtersToUse = isAdvanceFilter ? advanceFilters : filters;
  const selectedProfiles: string[] = filtersToUse['profile_filter'] || [];
  const isAccordionExpanded = expandedFilters.includes('profile_filter') || false;

  const extraOptions = useMemo(() => {
    const optionsArray = [];
    if (filterOptions?.includes('showDeleted'))
      optionsArray.push({
        label: 'Include Deleted Profiles',
        id: 'include-deleted-profiles',
        checked: showDeletedProfiles,
        onClick: (e: MouseEvent<HTMLButtonElement>) => handleCheckboxChange(e)
      });
    return optionsArray;
  }, [showDeletedProfiles, filterOptions]);

  const profiles = useMemo(() => {
    if (filtersToUse && filtersToUse.customer_filter?.length && customerList) {
      const filteredCustomerNames = filtersToUse.customer_filter.map((f: string) => customerList[f]);
      return Object.entries(allProfiles).reduce(
        (acc, [customer, customerProfiles]) => {
          if (filteredCustomerNames.includes(customer)) {
            acc[customer] = customerProfiles as CustomerProfile[];
          }
          return acc;
        },
        {} as Record<string, any[]>
      );
    } else {
      return allProfiles;
    }
  }, [allProfiles, customerList, filtersToUse?.customer_filter]);

  const filteredCustomerProfiles = useMemo(() => {
    const lowerSearch = searchTerm.toLowerCase();

    return Object.entries(profiles).map(([customer, customerProfiles]) => {
      const matchesCustomer = customer.toLowerCase().includes(lowerSearch);
      const filteredProfiles = customerProfiles?.filter((profile) => profile.name.toLowerCase().includes(lowerSearch));
      const isDisplayed = matchesCustomer || (filteredProfiles && filteredProfiles.length > 0);
      const hasSelections = customerProfiles?.some((profile) => selectedProfiles.includes(profile.id.toString()));
      const shouldExpand =
        expandedStates[customer] !== undefined
          ? expandedStates[customer]
          : !!hasSelections || (searchTerm !== '' && filteredProfiles && filteredProfiles.length > 0);

      return {
        customer,
        customerProfiles: matchesCustomer ? customerProfiles : filteredProfiles,
        isDisplayed,
        shouldExpand
      };
    });
  }, [searchTerm, selectedProfiles, expandedStates, profiles]);

  const profilesObjArray = Object.values(profiles).flatMap((customerProfiles) => customerProfiles?.map((profile) => profile));
  const profilesIdArray = Object.values(profiles).flatMap((customerProfiles) => customerProfiles?.map((profile) => profile.id));
  const isAllProfilesSelected = selectedProfiles.length === profilesIdArray.length;
  const isNotAllProfilesSelected = selectedProfiles.length > 0 && selectedProfiles.length !== profilesIdArray.length;

  const onInputChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    e.stopPropagation();
    setSearchTerm(e.target.value);
    setExpandedStates({});
  };

  const clearFilter = () => {
    setSearchTerm('');
    setExpandedStates({});
    searchBoxRef.current?.focus();
  };

  const handleExpandClick = (clientName: string, isExpanded: boolean) => {
    setExpandedStates((prev) => ({ ...prev, [clientName]: !isExpanded }));
  };

  const handleToggleProfile = (profileId: string) => {
    const newSelectedProfiles = new Set(selectedProfiles);
    if (newSelectedProfiles.has(profileId)) {
      newSelectedProfiles.delete(profileId);
    } else {
      newSelectedProfiles.add(profileId);
    }
    const newValue = { type: 'profile_filter', selections: Array.from(newSelectedProfiles) };
    if (isAdvanceFilter && onAdvanceFiltersChange) {
      onAdvanceFiltersChange(newValue);
    } else {
      dispatch(applyFilter({ ...newValue, debounceNextDataRequest: true }));
    }
  };

  const handleSelectAll = () => {
    const profilesArr = filteredCustomerProfiles
      .map((customersProfiles) => {
        if (!customersProfiles.isDisplayed) return [];
        const customerProfiles = customersProfiles.customerProfiles as any[];
        return customerProfiles?.map((profile) => profile.id.toString());
      })
      .flat();
    const newValue = { type: 'profile_filter', selections: profilesArr };
    if (isAdvanceFilter && onAdvanceFiltersChange) {
      onAdvanceFiltersChange(newValue);
    } else {
      dispatch(applyFilter(newValue));
    }
    setExpandedStates({});
  };

  const handleClearAll = () => {
    const newValue = { type: 'profile_filter', selections: [] };
    if (isAdvanceFilter && onAdvanceFiltersChange) {
      onAdvanceFiltersChange(newValue);
    } else {
      dispatch(applyFilter(newValue));
    }
    setExpandedStates({});
  };

  const handleCustomerSelect = (customerProfiles: any[], isFullySelected: boolean) => {
    const newSelectedProfiles = new Set(selectedProfiles);

    customerProfiles.forEach((profile) => {
      if (isFullySelected) {
        newSelectedProfiles.delete(profile.id.toString());
      } else {
        newSelectedProfiles.add(profile.id.toString());
      }
    });

    const newValue = { type: 'profile_filter', selections: Array.from(newSelectedProfiles) };
    if (isAdvanceFilter && onAdvanceFiltersChange) {
      onAdvanceFiltersChange(newValue);
    } else {
      dispatch(applyFilter(newValue));
    }
  };

  const handleCheckboxChange = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    if (!showDeletedProfiles && !deletedProfiles.length) dispatch(getDeletedProfiles());
    dispatch(setShowDeletedProfilesInJobsFilter(!showDeletedProfiles));
  };

  const handleClearSearchButtonClick = () => {
    setSearchTerm('');
  };

  const handleExpand = () => {
    setSelectedFiltersExpanded(true);
  };

  const handleCollapse = () => {
    setSelectedFiltersExpanded(false);
  };

  const renderAllButtons = () => {
    if (filteredCustomerProfiles.every((fcp) => !fcp.isDisplayed)) {
      return null;
    }

    let content = null;
    const profilesArr = Object.values(profiles).flatMap((customerProfiles) => customerProfiles?.map((profile) => profile.id));

    if (selectedProfiles.length === 0) {
      content = <SelectAllButton onClick={handleSelectAll}>Select All</SelectAllButton>;
    } else if (selectedProfiles.length === profilesArr.length) {
      content = <ClearAllButton onClick={handleClearAll}>Clear All</ClearAllButton>;
    } else if (selectedProfiles.length > 0 && selectedProfiles.length < profilesArr.length) {
      content = (
        <>
          <SelectAllButton onClick={handleSelectAll}>Select All</SelectAllButton>
          <Divider orientation="vertical" flexItem />
          <ClearAllButton onClick={handleClearAll}>Clear All</ClearAllButton>
        </>
      );
    }

    return <ButtonsContainer>{content}</ButtonsContainer>;
  };

  const chipsToDisplay = useMemo(() => {
    if (selectedFiltersExpanded) return { count: selectedProfiles.length, characterLength: 0 };

    let count = 0;
    let characterLength = 0;

    for (const curr of selectedProfiles) {
      const nextCount = count + 1;
      const nextCharacterLength = characterLength + profilesObjArray.find((pObj) => pObj.id === +curr)?.name?.length || 0;
      if (nextCharacterLength > 30 || nextCount > 3) {
        break;
      }

      count = nextCount;
      characterLength = nextCharacterLength;
    }

    return { count, characterLength };
  }, [selectedProfiles, selectedFiltersExpanded]);

  return isFiltersV2 && !isQuickFilters ? (
    <FilterAccordion
      label="Profiles"
      expanded={isAccordionExpanded}
      count={selectedProfiles?.length}
      showClear={selectedProfiles?.length > 0}
      focusElementOnExpand={inputRef}
      onClear={handleClearAll}
      onChange={() => onExpand?.('profile_filter')}
    >
      <StyledBox>
        <SelectedFiltersContainer>
          {selectedProfiles.slice(0, chipsToDisplay.count).map((sp) => (
            <SelectedFilterChip
              key={`selected-profile-${sp}`}
              label={profilesObjArray.find((pObj) => pObj.id === +sp)?.name || '-'}
              deleteIcon={<PolarisIcon id={sp} icon="close" height={16} />}
              onClick={() => handleToggleProfile(sp)}
              onDelete={() => handleToggleProfile(sp)}
            />
          ))}
          {selectedProfiles.length > chipsToDisplay.count && !selectedFiltersExpanded && (
            <Tooltip title="Show more">
              <ExpandButton variant="text" onClick={handleExpand}>
                ...
              </ExpandButton>
            </Tooltip>
          )}
          {selectedProfiles.length > 0 && <SelectedOptionsCount>{selectedProfiles.length}</SelectedOptionsCount>}
          {selectedFiltersExpanded && (
            <CollapseWrapper>
              <CollapseButton
                variant="text"
                size="small"
                color="default"
                startIcon={<PolarisIcon icon="subtract" height={16} width={8} />}
                onClick={handleCollapse}
              >
                Show less
              </CollapseButton>
            </CollapseWrapper>
          )}
        </SelectedFiltersContainer>
        <SearchField
          fullWidth
          value={searchTerm}
          label={`Search Profiles`}
          inputRef={inputRef}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {searchTerm.length > 0 ? (
                  <ClearSearchButton onClick={handleClearSearchButtonClick}>
                    <PolarisIcon icon="close_filled" />
                  </ClearSearchButton>
                ) : (
                  <PolarisIcon icon="search" />
                )}
              </InputAdornment>
            )
          }}
          onChange={onInputChange}
        />
        {!!extraOptions.length && (
          <ExtraOptionsBox expanded={{ optionsExpanded }} isFiltersV2={isFiltersV2}>
            <ExtraOptionsHeader
              onClick={() => {
                setOptionsExpanded(!optionsExpanded);
              }}
            >
              {optionsExpanded ? <PolarisIcon icon="up" /> : <PolarisIcon icon="down" />}
              <div>Additional Options</div>
            </ExtraOptionsHeader>
            <ExtraOptionsContent>
              {extraOptions.map((option) => (
                <div key={option.label}>
                  <Checkbox id={option.id} checked={option.checked} onClick={option.onClick} /> <label htmlFor={option.id}>{option.label}</label>
                </div>
              ))}
            </ExtraOptionsContent>
          </ExtraOptionsBox>
        )}
        <Grid container alignItems="center" justifyContent="flex-end" py={1}>
          {!isAllProfilesSelected && (
            <SelectAll
              value="select-all"
              control={<Checkbox indeterminate={isNotAllProfilesSelected} checked={isAllProfilesSelected} onChange={handleSelectAll} />}
              label="Select All"
              labelPlacement="start"
            />
          )}
        </Grid>
        <StyledAccordionList>
          {filteredCustomerProfiles.map((customersProfiles, index) => {
            if (!customersProfiles.isDisplayed) return null;
            const profilesToShow = customersProfiles.customerProfiles as any[];
            const isFullySelected = Boolean(customersProfiles.customerProfiles?.every((profile) => selectedProfiles.includes(profile.id.toString())));
            const isExpanded = !!customersProfiles.shouldExpand;

            return (
              <div key={`${customersProfiles.customer}-${index}`}>
                <ListItem
                  disablePadding
                  tabIndex={index + 1}
                  secondaryAction={
                    <IconButton edge="end" onClick={() => handleExpandClick(customersProfiles.customer, isExpanded)}>
                      {isExpanded ? <PolarisIcon icon="up" height={16} /> : <PolarisIcon icon="down" height={16} />}
                    </IconButton>
                  }
                >
                  <StyledListItemButton dense selected={isFullySelected} onClick={() => handleCustomerSelect(profilesToShow, isFullySelected)}>
                    <ListItemText
                      primary={
                        <StyledCustomerContainer>
                          <Typography>{customersProfiles.customer}</Typography>
                          <StyledBadge>{customersProfiles.customerProfiles?.length || 0}</StyledBadge>
                        </StyledCustomerContainer>
                      }
                    />
                  </StyledListItemButton>
                </ListItem>
                <Collapse in={isExpanded} timeout="auto">
                  {
                    // render a nested list only when it's expanded for performance reasons
                    isExpanded && (
                      <List component="div" disablePadding>
                        {profilesToShow.map((profile) => (
                          <Tooltip key={`${profile}-${profile.id}`} title={profile.name.length > 35 ? profile.name : ''}>
                            <ListItemButton
                              dense
                              onClick={() => handleToggleProfile(profile.id.toString())}
                              sx={(theme) => ({
                                padding: theme.spacing(0.5, 0.5, 0.5, 2)
                              })}
                            >
                              <ListItemText primary={truncateText(profile.name, 35)} />
                              <StyledListItemIcon>
                                <Checkbox edge="start" checked={selectedProfiles.includes(profile.id.toString())} />
                              </StyledListItemIcon>
                            </ListItemButton>
                          </Tooltip>
                        ))}
                      </List>
                    )
                  }
                </Collapse>
              </div>
            );
          })}
        </StyledAccordionList>
      </StyledBox>
    </FilterAccordion>
  ) : (
    <Grid container flexDirection="column">
      <SearchBox
        inputRef={searchBoxRef}
        role="combobox"
        aria-labelledby="select search"
        aria-haspopup="listbox"
        aria-expanded="true"
        aria-controls="select-dropdown"
        id="program_id"
        autoFocus
        name="filter search"
        autoComplete="off"
        variant="outlined"
        color="primary"
        size="small"
        label="Quick Search Profiles"
        value={searchTerm}
        onChange={onInputChange}
        InputProps={{
          inputProps: {
            'data-testid': 'filter-search-input'
          },
          tabIndex: 0,
          endAdornment: searchTerm.length ? (
            <IconButton onClick={clearFilter} tabIndex={Object.keys(filteredCustomerProfiles).length + 1}>
              <ClearButton show icon="close" height={18} />
            </IconButton>
          ) : undefined
        }}
      />
      {!!extraOptions.length && (
        <ExtraOptionsBox expanded={{ optionsExpanded }}>
          <ExtraOptionsHeader
            onClick={() => {
              setOptionsExpanded(!optionsExpanded);
            }}
          >
            {optionsExpanded ? <PolarisIcon icon="up" /> : <PolarisIcon icon="down" />}
            <div>Additional Options</div>
          </ExtraOptionsHeader>
          <ExtraOptionsContent>
            {extraOptions.map((option) => (
              <div key={option.label}>
                <Checkbox id={option.id} checked={option.checked} onClick={option.onClick} /> <label htmlFor={option.id}>{option.label}</label>
              </div>
            ))}
          </ExtraOptionsContent>
        </ExtraOptionsBox>
      )}
      <Grid container flexDirection="column">
        {renderAllButtons()}
        <Grid container flexDirection="column" maxHeight={300} overflow="auto" minWidth={200} maxWidth={300} flexWrap="nowrap">
          {filteredCustomerProfiles.map((customersProfiles, index) => {
            if (!customersProfiles.isDisplayed) return null;
            const profilesToShow = customersProfiles.customerProfiles as any[];
            const isFullySelected = Boolean(customersProfiles.customerProfiles?.every((profile) => selectedProfiles.includes(profile.id.toString())));
            const isExpanded = !!customersProfiles.shouldExpand;

            return (
              <Grid container key={`${customersProfiles.customer}-${index}`} flexDirection="column">
                <StyledCustomerWrapper isSelected={isFullySelected}>
                  <StyledCustomerContainer onClick={() => handleCustomerSelect(profilesToShow, isFullySelected)}>
                    <Typography>{customersProfiles.customer}</Typography>
                    <StyledBadge>{customersProfiles.customerProfiles?.length || 0}</StyledBadge>
                  </StyledCustomerContainer>
                  <IconButton
                    aria-label={isExpanded ? 'collapse' : 'expand'}
                    onClick={() => handleExpandClick(customersProfiles.customer, isExpanded)}
                  >
                    {isExpanded ? <PolarisIcon icon="up" /> : <PolarisIcon icon="down" />}
                  </IconButton>
                </StyledCustomerWrapper>
                <Collapse in={isExpanded}>
                  <StyledList>
                    {profilesToShow.map((profile) => (
                      <Tooltip key={`${profile}-${profile.id}`} title={profile.name.length > 35 ? profile.name : ''}>
                        <StyledListItem
                          onClick={() => handleToggleProfile(profile.id.toString())}
                          isSelected={selectedProfiles.includes(profile.id.toString())}
                        >
                          <ListItemText primary={truncateText(profile.name, 35)} />
                        </StyledListItem>
                      </Tooltip>
                    ))}
                  </StyledList>
                </Collapse>
              </Grid>
            );
          })}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default FilterProfiles;

const SearchBox = styled(TextField)`
  && {
    margin: ${(props) => props.theme.spacing(2)};
    width: calc(100% - ${(props) => props.theme.spacing(4)});
    min-width: 150px;
    max-width: 300px;

    & .MuiInputBase-root {
      padding-right: 0;
      font-size: 1rem;
      min-height: 50px;
    }

    & .MuiInputLabel-root {
      font-size: 1rem;
      line-height: 2rem;
    }
  }
`;

const ClearButton = styled(PolarisIcon)<{ show: boolean }>`
  ${(props) =>
    props.show
      ? `
    opacity: 1;
  `
      : `
    opacity: 0;
  `};

  margin: ${(props) => props.theme.spacing(1)};
  transition: ease opacity 250ms;

  & svg {
    font-size: 1.2rem;
  }
`;

const ButtonsContainer = styled(Grid)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  columnGap: theme.spacing(1),
  padding: theme.spacing(2, 2),
  paddingTop: 0
}));

const SelectAllButton = styled(Link)`
  color: ${(props) => props.theme.palette.primary.main};
  transition: all 0.3s ease;
  &:hover {
    color: ${(props) => props.theme.palette.primary.light};
  }
`;

const ClearAllButton = styled(Link)`
  color: ${(props) => props.theme.palette.grey[600]};
  transition: all 0.3s ease;
  &:hover {
    color: ${(props) => props.theme.palette.grey[400]};
  }
`;

const StyledCustomerWrapper = styled(Grid, {
  shouldForwardProp: (prop) => prop !== 'isSelected'
})<{ isSelected: boolean }>(({ theme, isSelected }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(0.1, 2),
  backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.16) : 'inherit'
}));

const StyledCustomerContainer = styled(Grid)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  width: 'fit-content',
  columnGap: theme.spacing(1),
  cursor: 'pointer',
  '& > p': {
    transition: 'all 0.3s ease'
  },
  '&:hover > p': {
    color: theme.palette.primary.main
  }
}));

const StyledBadge = styled.div(({ theme }) => ({
  width: 18,
  height: 18,
  lineHeight: '18px',
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.getContrastText(theme.palette.primary.main),
  fontSize: theme.typography.caption.fontSize,
  textAlign: 'center'
}));

const StyledList = styled(List)(() => ({
  padding: 0,
  display: 'flex',
  flexDirection: 'column'
}));

const StyledListItem = styled(ListItem, { shouldForwardProp: (propName) => !['isSelected'].includes(propName) })<{ isSelected: boolean }>(
  ({ theme, isSelected }) => ({
    paddingTop: theme.spacing(0.25),
    paddingBottom: theme.spacing(0.25),
    paddingLeft: theme.spacing(4),
    cursor: 'pointer',
    color: alpha(theme.palette.text.primary, 0.85),
    backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.16) : 'inherit',
    '&.MuiListItem-root': {
      '&:hover': {
        backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.32) : theme.palette.action.hover
      }
    }
  })
);

const ExtraOptionsBox = styled(Box, { shouldForwardProp: (prop) => prop !== 'isFiltersV2' })<{
  expanded: { optionsExpanded: boolean };
  isFiltersV2?: boolean;
}>(({ expanded, isFiltersV2, theme }) => ({
  flexDirection: 'column',
  backgroundColor: alpha(theme.palette.background.default, 1),
  height: expanded.optionsExpanded ? theme.spacing(8) : theme.spacing(3),
  position: 'relative',
  transform: `translateY(-${theme.spacing(2)}) translateX(${theme.spacing(2)})`,
  width: `calc(100% - ${theme.spacing(4)})`,
  borderRadius: `0 0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px`,
  overflow: 'hidden',
  transition: 'height 0.3s ease',
  marginTop: theme.spacing(isFiltersV2 ? 3 : 0)
}));

const ExtraOptionsHeader = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  width: '100%',
  height: theme.spacing(3),
  backgroundColor: alpha(theme.palette.background.default, 1),
  display: 'flex',
  alignItems: 'center',
  columnGap: theme.spacing(1),
  justifyContent: 'center',
  cursor: 'pointer'
}));

const ExtraOptionsContent = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: theme.spacing(3),
  left: 0,
  width: '100%',
  borderBottom: `1px solid ${theme.palette.divider}`,
  '& > div': {
    display: 'flex',
    alignItems: 'center',
    columnGap: theme.spacing(1),
    cursor: 'pointer'
  },
  '& label': {
    cursor: 'pointer'
  }
}));

const SelectAll = styled(FormControlLabel)(() => ({
  marginLeft: 0,
  marginRight: 0
}));

const StyledAccordionList = styled(List)(({ theme }) => ({
  overflow: 'auto',
  maxHeight: 200,
  padding: 0,
  '& > div:not(:last-of-type)': {
    marginBottom: theme.spacing(0.5)
  }
}));

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  padding: theme.spacing(0.5, 0.5, 0.5, 1)
}));

const StyledListItemIcon = styled(ListItemIcon)(() => ({
  minWidth: 'auto'
}));
